import { useState } from 'react'
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    type LegendPayload,
} from 'recharts'
import type { TimeseriesPoint } from '~/api/types'
import {
    KRDS,
    CHART_GRID,
    CHART_AXIS,
    tooltipStyles,
} from '~/components/charts/chart-colors'

// 시계열 추이 차트. recharts 멀티 시리즈 에어리어(상담·인증·유지·피드백·모니터링) + KRDS 색/격자/툴팁.
// 의미색 일관: 상담 primary · 인증 info · 유지 warning · 피드백 success · 모니터링 point.

// 멀티 시리즈 정의(키·라벨·색). 통계 뷰의 의미색과 일관되게 둔다.
// 모니터링은 나머지 4색과 겹치지 않도록 point(자홍) 사용.
const SERIES = [
    { key: 'consultations', label: '상담', color: KRDS.primary },
    { key: 'assessments', label: '인증', color: KRDS.info },
    { key: 'maintenance', label: '유지', color: KRDS.warning },
    { key: 'feedback', label: '피드백', color: KRDS.success },
    { key: 'monitoring', label: '모니터링', color: KRDS.point },
] as const

// 날짜(YYYY-MM-DD) → 축 표시용 'M/D'. 기간이 길면 라벨이 빽빽해지므로 짧게.
function shortDate(iso: string): string {
    const [, m, d] = iso.split('-')
    return `${Number(m)}/${Number(d)}`
}

interface TrendLinesProps {
    data: TimeseriesPoint[]
    height?: number
}

// 일별 활동 추이(상담·인증·유지·피드백·모니터링 멀티 시리즈 에어리어).
// 범례 클릭 = 해당 시리즈만 격리(다시 클릭·격리 항목 재클릭 → 전체 표시).
export function TrendLines({ data, height = 280 }: TrendLinesProps) {
    const [isolated, setIsolated] = useState<string | null>(null)
    const toggleIsolate = (entry: LegendPayload) => {
        const key = String(entry.dataKey ?? '')
        setIsolated((cur) => (cur === key ? null : key))
    }
    return (
        <ResponsiveContainer width='100%' height={height}>
            <AreaChart
                data={data}
                margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <defs>
                    {SERIES.map((s) => (
                        <linearGradient
                            key={s.key}
                            id={`grad-${s.key}`}
                            x1='0'
                            y1='0'
                            x2='0'
                            y2='1'>
                            <stop
                                offset='0%'
                                stopColor={s.color}
                                stopOpacity={0.24}
                            />
                            <stop
                                offset='100%'
                                stopColor={s.color}
                                stopOpacity={0.02}
                            />
                        </linearGradient>
                    ))}
                </defs>
                <CartesianGrid stroke={CHART_GRID} vertical={false} />
                <XAxis
                    dataKey='date'
                    tickFormatter={shortDate}
                    tick={{ fill: CHART_AXIS, fontSize: 12 }}
                    axisLine={{ stroke: CHART_GRID }}
                    tickLine={false}
                    minTickGap={24}
                />
                <YAxis
                    type='number'
                    tick={{ fill: CHART_AXIS, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                    width={32}
                />
                <Tooltip
                    formatter={(v, name) => [`${v ?? 0}건`, name]}
                    contentStyle={tooltipStyles.contentStyle}
                    itemStyle={tooltipStyles.itemStyle}
                    labelStyle={tooltipStyles.labelStyle}
                    cursor={{ stroke: CHART_GRID }}
                />
                <Legend
                    wrapperStyle={{
                        fontSize: 13,
                        paddingTop: 8,
                        cursor: 'pointer',
                    }}
                    iconType='plainline'
                    onClick={toggleIsolate}
                    formatter={(value, entry) => {
                        const dim =
                            isolated !== null &&
                            isolated !== String(entry.dataKey ?? '')
                        return (
                            <span style={{ opacity: dim ? 0.35 : 1 }}>
                                {value}
                            </span>
                        )
                    }}
                />
                {SERIES.map((s) => (
                    <Area
                        key={s.key}
                        type='monotone'
                        dataKey={s.key}
                        name={s.label}
                        stroke={s.color}
                        strokeWidth={2}
                        fill={`url(#grad-${s.key})`}
                        dot={false}
                        isAnimationActive={false}
                        hide={isolated !== null && isolated !== s.key}
                    />
                ))}
            </AreaChart>
        </ResponsiveContainer>
    )
}

interface SeriesAreaProps {
    /** 일별 버킷. dataKey 값을 단일 시리즈 에어리어로 그린다. */
    data: TimeseriesPoint[]
    /** TimeseriesPoint의 숫자 키(예: 'maintenance'). */
    dataKey: keyof TimeseriesPoint
    /** 시리즈 라벨(툴팁). */
    name: string
    /** 선/면 색(KRDS 의미색). */
    color?: string
    height?: number
}

// 단일 시리즈 일별 추이 에어리어. 서비스 개요 탭의 "일별 OO 추이"에 재사용.
export function SeriesArea({
    data,
    dataKey,
    name,
    color = KRDS.primary,
    height = 240,
}: SeriesAreaProps) {
    const gradId = `grad-series-${String(dataKey)}`
    return (
        <ResponsiveContainer width='100%' height={height}>
            <AreaChart
                data={data}
                margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <defs>
                    <linearGradient id={gradId} x1='0' y1='0' x2='0' y2='1'>
                        <stop
                            offset='0%'
                            stopColor={color}
                            stopOpacity={0.24}
                        />
                        <stop
                            offset='100%'
                            stopColor={color}
                            stopOpacity={0.02}
                        />
                    </linearGradient>
                </defs>
                <CartesianGrid stroke={CHART_GRID} vertical={false} />
                <XAxis
                    dataKey='date'
                    tickFormatter={shortDate}
                    tick={{ fill: CHART_AXIS, fontSize: 12 }}
                    axisLine={{ stroke: CHART_GRID }}
                    tickLine={false}
                    minTickGap={24}
                />
                <YAxis
                    type='number'
                    tick={{ fill: CHART_AXIS, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                    width={32}
                />
                <Tooltip
                    formatter={(v) => [`${v ?? 0}건`, name]}
                    contentStyle={tooltipStyles.contentStyle}
                    itemStyle={tooltipStyles.itemStyle}
                    labelStyle={tooltipStyles.labelStyle}
                    cursor={{ stroke: CHART_GRID }}
                />
                <Area
                    type='monotone'
                    dataKey={dataKey}
                    name={name}
                    stroke={color}
                    strokeWidth={2}
                    fill={`url(#${gradId})`}
                    dot={false}
                    isAnimationActive={false}
                />
            </AreaChart>
        </ResponsiveContainer>
    )
}

interface TrendBarsProps {
    /** 일별 총 활동(상담+인증+유지+피드백) 히스토그램 데이터. */
    data: TimeseriesPoint[]
    height?: number
}

// 일별 총 활동 히스토그램(막대). 트래픽(totalEvents)을 날짜별로 본다.
export function TrendBars({ data, height = 220 }: TrendBarsProps) {
    const bars = data.map((p) => ({
        date: p.date,
        total: p.consultations + p.assessments + p.maintenance + p.feedback,
    }))
    return (
        <ResponsiveContainer width='100%' height={height}>
            <BarChart
                data={bars}
                margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={CHART_GRID} vertical={false} />
                <XAxis
                    dataKey='date'
                    tickFormatter={shortDate}
                    tick={{ fill: CHART_AXIS, fontSize: 12 }}
                    axisLine={{ stroke: CHART_GRID }}
                    tickLine={false}
                    minTickGap={24}
                />
                <YAxis
                    type='number'
                    tick={{ fill: CHART_AXIS, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                    width={32}
                />
                <Tooltip
                    formatter={(v) => [`${v ?? 0}건`, '총 활동']}
                    contentStyle={tooltipStyles.contentStyle}
                    itemStyle={tooltipStyles.itemStyle}
                    labelStyle={tooltipStyles.labelStyle}
                    cursor={tooltipStyles.cursor}
                />
                <Bar
                    dataKey='total'
                    fill={KRDS.primary}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={28}
                    isAnimationActive={false}
                />
            </BarChart>
        </ResponsiveContainer>
    )
}
